import React, { useState, useEffect, useRef } from 'react';
import { search as searchApi } from '../lib/api';
import { Search, FileText, Filter, X } from 'lucide-react';
import { format } from 'date-fns';

const DOC_TYPES = ['Resolution','Ordinance','Meeting Minutes','Memorandum','Map','Contract','Report','Other'];
const DEPARTMENTS = ['City Clerk','City Attorney','Information Technology','Public Works','Planning','Finance','Parks & Recreation','Police','Fire','City Manager'];

function TypeBadge({ type }) {
  const colors = {
    'Resolution': 'bg-blue-50 text-blue-700',
    'Ordinance': 'bg-purple-50 text-purple-700',
    'Meeting Minutes': 'bg-teal-50 text-teal-700',
    'Map': 'bg-amber-50 text-amber-700',
    'Report': 'bg-green-50 text-green-700',
  };
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${colors[type] || 'bg-gray-100 text-gray-600'}`}>{type}</span>;
}

export default function SearchPage() {
  const [q, setQ] = useState('');
  const [filters, setFilters] = useState({ type: '', department: '', from: '', to: '' });
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const doSearch = async (newPage = 1) => {
    setLoading(true);
    setPage(newPage);
    try {
      const { data } = await searchApi.query({ q, ...filters, page: newPage, limit: 15 });
      setResults(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => { if (e.key === 'Enter') doSearch(1); };
  const clearFilters = () => { setFilters({ type: '', department: '', from: '', to: '' }); };
  const hasFilters = Object.values(filters).some(Boolean);

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-gray-900">Search records</h1>
        <p className="text-sm text-gray-500 mt-1">Full-text search across all indexed documents including OCR content</p>
      </div>

      {/* Search bar */}
      <div className="flex gap-3 mb-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input ref={inputRef} value={q} onChange={e => setQ(e.target.value)} onKeyDown={handleKey}
            className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50 bg-white"
            placeholder="Search by title, content, type, department..." />
        </div>
        <button onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 px-4 py-3 rounded-xl border text-sm font-medium transition-all
            ${showFilters || hasFilters ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
          <Filter size={15} />
          Filters
          {hasFilters && <span className="w-4 h-4 bg-blue-600 text-white text-xs rounded-full flex items-center justify-center">{Object.values(filters).filter(Boolean).length}</span>}
        </button>
        <button onClick={() => doSearch(1)} disabled={loading}
          className="px-6 py-3 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-60">
          {loading ? '...' : 'Search'}
        </button>
      </div>

      {/* Filters panel */}
      {showFilters && (
        <div className="bg-white rounded-xl border border-gray-100 p-4 mb-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">Document type</label>
            <select value={filters.type} onChange={e => setFilters(f => ({ ...f, type: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:border-blue-400">
              <option value="">Any</option>
              {DOC_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">Department</label>
            <select value={filters.department} onChange={e => setFilters(f => ({ ...f, department: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:border-blue-400">
              <option value="">Any</option>
              {DEPARTMENTS.map(d => <option key={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">Date from</label>
            <input type="date" value={filters.from} onChange={e => setFilters(f => ({ ...f, from: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-blue-400" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">Date to</label>
            <input type="date" value={filters.to} onChange={e => setFilters(f => ({ ...f, to: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-blue-400" />
          </div>
          {hasFilters && (
            <button onClick={clearFilters} className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 col-span-full">
              <X size={12} /> Clear all filters
            </button>
          )}
        </div>
      )}

      {/* Results */}
      {results === null && !loading && (
        <div className="text-center py-16 text-gray-400">
          <Search size={32} className="mx-auto mb-3 opacity-30" />
          <div className="text-sm">Enter a search query above to find records</div>
          <div className="text-xs mt-1">Searches title, content, OCR text, and metadata</div>
        </div>
      )}

      {loading && (
        <div className="space-y-3">
          {[1,2,3].map(i => <div key={i} className="bg-white rounded-xl border border-gray-100 p-5 animate-pulse">
            <div className="h-4 bg-gray-100 rounded w-2/3 mb-2" />
            <div className="h-3 bg-gray-50 rounded w-1/3" />
          </div>)}
        </div>
      )}

      {results && !loading && (
        <>
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm text-gray-500">
              {results.total === 0 ? 'No results' : `${results.total.toLocaleString()} result${results.total !== 1 ? 's' : ''}${q ? ` for "${q}"` : ''}`}
            </div>
          </div>

          {results.results.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-gray-100">
              <div className="text-sm text-gray-400">No records matched your search</div>
              <div className="text-xs text-gray-300 mt-1">Try broader terms or remove filters</div>
            </div>
          ) : (
            <div className="space-y-3">
              {results.results.map(r => (
                <div key={r.id} className="bg-white rounded-xl border border-gray-100 p-5 hover:border-gray-200 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1.5">
                        <TypeBadge type={r.document_type} />
                        {r.department && <span className="text-xs text-gray-400">{r.department}</span>}
                        {r.status === 'published' && (
                          <span className="text-xs bg-green-50 text-green-600 px-2 py-0.5 rounded-full">published</span>
                        )}
                      </div>
                      <h3 className="text-sm font-medium text-gray-900">{r.title}</h3>
                      {r.snippet && (
                        <p className="text-xs text-gray-500 mt-1.5 leading-relaxed"
                          dangerouslySetInnerHTML={{ __html: r.snippet.replace(/<b>/g, '<mark class="bg-yellow-100 text-yellow-800 px-0.5 rounded">').replace(/<\/b>/g, '</mark>') }} />
                      )}
                    </div>
                    <div className="text-xs text-gray-400 whitespace-nowrap flex-shrink-0">
                      {r.record_date ? format(new Date(r.record_date), 'MMM d, yyyy') : format(new Date(r.created_at), 'MMM d, yyyy')}
                    </div>
                  </div>
                  <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-50">
                    <div className="flex flex-wrap gap-1.5">
                      {r.tags?.map(tag => (
                        <span key={tag} className="text-xs bg-gray-50 text-gray-500 px-2 py-0.5 rounded-full border border-gray-100">{tag}</span>
                      ))}
                    </div>
                    <a href={`${process.env.REACT_APP_API_URL?.replace(/\/api$/, '') || ''}/uploads/${r.file_path}`}
                      target="_blank" rel="noopener noreferrer"
                      className="ml-auto text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1">
                      <FileText size={12} /> View document
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {results.total > 15 && (
            <div className="flex items-center justify-center gap-3 mt-6">
              <button disabled={page === 1} onClick={() => doSearch(page - 1)}
                className="px-4 py-2 text-sm rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">← Previous</button>
              <span className="text-sm text-gray-500">Page {page} of {Math.ceil(results.total / 15)}</span>
              <button disabled={page * 15 >= results.total} onClick={() => doSearch(page + 1)}
                className="px-4 py-2 text-sm rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">Next →</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
